/**
 * Cloud Infrastructure Cost Tracker
 * Lightweight tracking of cloud operations for optimization insights
 *
 * Usage:
 *   import { trackFsRead, trackStorageDownload, FEATURES } from '@services/cost-tracker';
 *
 *   // Track Firestore read
 *   trackFsRead('todos', FEATURES.TODAY_VIEW, 50);
 *
 *   // Track storage download
 *   trackStorageDownload(FEATURES.ATTACHMENTS, file.size);
 *
 *   // Track RTDB listener
 *   trackRtdbAttach(FEATURES.CLOUD_SYNC);
 *
 *   // Track function call
 *   const start = performance.now();
 *   await callable();
 *   trackFnCall('fetchCollectionData', FEATURES.TODAY_VIEW, performance.now() - start);
 */

// Core tracking functions
export {
  trackFsRead,
  trackFsWrite,
  trackFsDelete,
  trackStorageUpload,
  trackStorageDownload,
  trackRtdbAttach,
  trackRtdbData,
  trackRtdbChange,
  trackFnCall,
  resetSession,
  getState,
  getSessionDuration,
} from './cost-tracker';

// Analysis functions (on-demand)
export {
  getSummary,
  getTopOperations,
  getFirestoreByCollection,
  getFirestoreByFeature,
  getStorageBreakdown,
  getRtdbBreakdown,
  getFunctionBreakdown,
  getRtdbChangeEvents,
  getRtdbChangesByCollection,
} from './cost-tracker-analysis';

// Feature constants
export { FEATURES, type Feature } from './cost-tracker.features';

// Pricing utilities
export { PRICING, formatBytes, formatCost, formatDuration } from './pricing';

// Types
export type {
  CostTrackerState,
  FirestoreMetrics,
  StorageMetrics,
  RtdbMetrics,
  FunctionMetrics,
  InfrastructureSummary,
  RankedOperation,
  StorageBreakdown,
  RtdbFeatureBreakdown,
  FirestoreCollectionBreakdown,
  FunctionBreakdown,
  RtdbChangeEvent,
  RtdbCollectionChanges,
} from './cost-tracker.types';
